import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Copy, Check, Share2, MessageCircle } from "lucide-react";
import { getMyReferral, saveMyReferral, buildShareUrl } from "@/utils/referral";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const shareText = (code) =>
  `Mira esta tienda de ropa original americana a precios bajos: ${buildShareUrl(code)}`;

export const ReferralSection = () => {
  const [me, setMe] = useState(getMyReferral());
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/referrals`, { name, whatsapp });
      saveMyReferral(res.data);
      setMe(res.data);
      toast.success("Tu enlace esta listo");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo crear tu enlace");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    const url = buildShareUrl(me.code);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const input = document.createElement("input");
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    toast.success("Enlace copiado");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Original y Barato", text: shareText(me.code), url: buildShareUrl(me.code) });
      } catch (err) {
        if (err?.name !== "AbortError") handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  const handleWhatsAppShare = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText(me.code))}`, "_blank");
  };

  return (
    <section id="referidos" className="py-12 sm:py-16 px-4" data-testid="referral-section">
      <div className="max-w-3xl mx-auto glass-card p-6 sm:p-8 border-[#B026FF]/40">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-[#B026FF]/10 flex items-center justify-center mx-auto mb-4">
            <Gift className="w-7 h-7 text-[#B026FF]" />
          </div>
          <h2 className="font-oswald text-2xl sm:text-3xl font-bold text-white uppercase tracking-wider">
            Comparte y Gana $50
          </h2>
          <p className="text-[#A0A0A0] text-base mt-2 max-w-md mx-auto">
            Comparte tu enlace con amigos y familia. Cuando alguien compre con tu enlace, te pagamos <span className="text-[#00FF66] font-bold">$50 MXN</span>.
          </p>
        </div>

        {!me ? (
          <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3" data-testid="referral-form">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tu nombre"
              required
              className="bg-[#050505] border border-white/10 text-white px-4 py-3 text-base rounded-xl focus:border-[#B026FF] focus:outline-none placeholder-[#A0A0A0]/50"
              data-testid="referral-name-input"
            />
            <input
              type="tel"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="Tu WhatsApp (10 digitos)"
              required
              className="bg-[#050505] border border-white/10 text-white px-4 py-3 text-base rounded-xl focus:border-[#B026FF] focus:outline-none placeholder-[#A0A0A0]/50"
              data-testid="referral-phone-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-[#B026FF] text-white px-6 py-3 rounded-xl font-bold text-base uppercase tracking-wide hover:shadow-[0_0_20px_rgba(176,38,255,0.4)] transition-all duration-300 disabled:opacity-50"
              data-testid="referral-submit-btn"
            >
              {loading ? "..." : "Crear mi enlace"}
            </button>
          </form>
        ) : (
          <div className="space-y-4" data-testid="referral-result">
            <div className="bg-[#050505] rounded-xl p-4 text-center border border-white/5">
              <p className="text-xs text-[#A0A0A0] uppercase tracking-wider mb-1">Tu codigo, {me.name}</p>
              <p className="font-oswald text-3xl font-bold text-[#00FF66] tracking-widest" data-testid="referral-code">{me.code}</p>
              <p className="text-sm text-[#A0A0A0] mt-2 break-all" data-testid="referral-link">{buildShareUrl(me.code)}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button onClick={handleWhatsAppShare} className="bg-[#25D366] text-white py-3 rounded-xl font-bold text-base flex items-center justify-center gap-2 hover:shadow-[0_0_16px_rgba(37,211,102,0.4)] transition-all" data-testid="referral-share-whatsapp-btn">
                <MessageCircle className="w-5 h-5" /> WhatsApp
              </button>
              <button onClick={handleCopy} className="bg-[#121212] border border-white/10 text-white py-3 rounded-xl font-bold text-base flex items-center justify-center gap-2 hover:border-[#00FF66] transition-all" data-testid="referral-copy-btn">
                {copied ? <Check className="w-5 h-5 text-[#00FF66]" /> : <Copy className="w-5 h-5" />} Copiar enlace
              </button>
              <button onClick={handleNativeShare} className="bg-[#121212] border border-white/10 text-white py-3 rounded-xl font-bold text-base flex items-center justify-center gap-2 hover:border-[#B026FF] transition-all" data-testid="referral-share-btn">
                <Share2 className="w-5 h-5" /> Compartir
              </button>
            </div>
            <p className="text-xs text-[#A0A0A0] text-center">
              Cuando tu amigo nos escriba por WhatsApp, su mensaje incluira tu codigo automaticamente.
            </p>
            <Link to={`/referido/${me.code}`} className="block text-center text-[#B026FF] font-semibold hover:underline" data-testid="referral-earnings-link">
              Ver cuanto he ganado
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
